import { createApi, fakeBaseQuery, SkipToken } from "@reduxjs/toolkit/dist/query/react";
import { storage } from "@/firebase";
import { ApiError } from "@/types/api";
import { ref, list, uploadBytes, getDownloadURL, deleteObject } from "@firebase/storage";
import { extractExtension } from "@/utils/media";

export interface MediaArquments {
  templateId?: string | SkipToken;
  organizationId?: string | SkipToken;
  nextPageToken?: string;
}

export interface MediaResponse {
  images: string[];
  nextPageToken: string | null;
}

const MAX_ITEMS = 16;

export const mediaApi = createApi({
  reducerPath: "media",
  baseQuery: fakeBaseQuery<ApiError>(),
  tagTypes: ["media"],
  endpoints: (builder) => ({
    fetchImages: builder.query<MediaResponse, MediaArquments>({
      queryFn: (async ({ organizationId, templateId, nextPageToken }: MediaArquments) => {
        if (!organizationId && !templateId) {
          return {
            error: {
              status: 404,
              statusText: "Imágenes no encontradas",
            },
          };
        }

        const images = [];
        const folderRef = ref(storage, templateId ? `images/templates/${String(templateId)}` : `images/organizations/${String(organizationId)}`);
        const response = await list(folderRef, { maxResults: MAX_ITEMS, pageToken: nextPageToken });

        for (const item of response.items) {
          images.push(item.name);
        }

        return {
          data: {
            images,
            nextPageToken: response?.nextPageToken ?? null,
          },
        };
      }) as any,
      providesTags: ["media"],
      keepUnusedDataFor: 300,
    }),
    uploadImage: builder.mutation<{ fileName: string; url: string }, { file: File; organizationId?: string | SkipToken; templateId?: string | SkipToken }>({
      queryFn: (async ({ file, organizationId, templateId }: { file: File; organizationId?: string | SkipToken; templateId?: string | SkipToken }) => {
        if (!file || (!organizationId && !templateId)) {
          return {
            error: {
              status: 500,
              statusText: "Error al cargar el archivo",
            },
          };
        }

        const fileRef = ref(storage, templateId ? `images/templates/${String(templateId)}/${file.name}` : `images/organizations/${String(organizationId)}/${file.name}`);

        try {
          await getDownloadURL(fileRef);

          return {
            error: {
              status: 409,
              statusText: "El archivo ya existe",
            },
          };
        } catch (error) {
          const snapshot = await uploadBytes(fileRef, file);
          const url = await getDownloadURL(snapshot.ref);

          return {
            data: {
              fileName: file.name,
              url,
            },
          };
        }
      }) as any,
      invalidatesTags: ["media"],
    }),
    deleteImage: builder.mutation<{ fileName: string }, { fileNames: string[]; organizationId?: string | SkipToken; templateId?: string | SkipToken }>({
      queryFn: (async ({ organizationId, templateId, fileNames }: { fileNames: string[]; organizationId?: string | SkipToken; templateId?: string | SkipToken }) => {
        if ((!organizationId && !templateId) || !fileNames) {
          return {
            error: {
              status: 400,
              statusText: "Error al eliminar los archivos",
            },
          };
        }

        try {
          for await (const fileName of fileNames) {
            // Original
            const fileRef = ref(storage, templateId ? `images/templates/${String(templateId)}/${fileName}` : `images/organizations/${String(organizationId)}/${fileName}`);
            await deleteObject(fileRef);
            // Thumbs
            const thumb150x150Ref = ref(
              storage,
              templateId
                ? `images/templates/${String(templateId)}/thumbs/${extractExtension(fileName)}_150x150.webp`
                : `images/organizations/${String(organizationId)}/thumbs/${extractExtension(fileName)}_150x150.webp`
            );
            await deleteObject(thumb150x150Ref);
            const thumb800x500Ref = ref(
              storage,
              templateId
                ? `images/templates/${String(templateId)}/thumbs/${extractExtension(fileName)}_800x500.webp`
                : `images/organizations/${String(organizationId)}/thumbs/${extractExtension(fileName)}_800x500.webp`
            );
            await deleteObject(thumb800x500Ref);
          }

          return {
            data: {
              statusCode: 200,
            },
          };
        } catch (error) {
          if ((error as any)?.code === "storage/object-not-found") {
            return {
              error: {
                statusText: "Archivo no encontrado",
                statusCode: 404,
              },
            };
          }
          return {
            error: {
              statusCode: 500,
            },
          };
        }
      }) as any,
      invalidatesTags: ["media"],
    }),
  }),
});

export const { useFetchImagesQuery, useLazyFetchImagesQuery, useUploadImageMutation, useDeleteImageMutation } = mediaApi;
